@if (machineTab) {
  <section class="machine">
    @if (!machineTab.isSupported) {
      <div class="notify notify__danger machine__notice">
        <i class="agres-alert-triangle" aria-hidden="true"></i>
        <p>
          {{ 'monitoring.tabs.machine.unsupported-version' | translate: { version: machineTab.schemaVersion } }}
        </p>
      </div>
    } @else {
      <div class="machine__status">
        <span class="machine__link machine__link--{{ linkModifier }}">
          @if (machineTab.link === linkState.NoCommunication) {
            <i class="agres-wifi-off" aria-hidden="true"></i>
          } @else {
            <span class="machine__link-dot"></span>
          }
          {{ machineTab.linkKey | translate }}
        </span>
        <span class="machine__status-item">
          {{ 'monitoring.tabs.machine.label-updated-at' | translate }}
          <strong>{{ machineTab.receivedAt | date: timeFormat }}</strong>
        </span>
        @if (isEngineDetected && machineTab.engineHours) {
          <span class="machine__status-item">
            {{ machineTab.engineHours.shortLabelKey | translate }}
            @if (machineTab.engineHours.state === signalState.Valid) {
              <strong
                >{{ machineTab.engineHours.value | numberFormat: machineTab.engineHours.decimals }}
                {{ machineTab.engineHours.unit }}</strong
              >
            } @else {
              <ng-container
                [ngTemplateOutlet]="signalStateText"
                [ngTemplateOutletContext]="{ $implicit: machineTab.engineHours }"></ng-container>
            }
          </span>
        }
        @if (machineTab.activeDtcCount > 0) {
          <button type="button" class="machine__faults-shortcut" (click)="openFaults()">
            <i class="agres-alert-triangle" aria-hidden="true"></i>
            {{
              pluralKey('monitoring.tabs.machine.active-faults', machineTab.activeDtcCount)
                | translate: { count: machineTab.activeDtcCount }
            }}
          </button>
        }
      </div>

      @if (machineTab.isOutdated) {
        <div class="notify notify__warning machine__notice">
          <i class="agres-clock" aria-hidden="true"></i>
          <p>{{ 'monitoring.tabs.machine.outdated' | translate: { seconds: outdatedThresholdSeconds } }}</p>
        </div>
      }

      @if (isEngineDetected) {
        <ul class="machine__kpis">
          @for (kpi of machineTab.kpis; track kpi.spn) {
            <li class="machine__kpi">
              <span class="machine__kpi-label">{{ kpi.kpiLabelKey | translate }}</span>
              <span class="machine__kpi-value">
                @if (kpi.state === signalState.Valid) {
                  @if (kpi.value !== null) {
                    <strong class="machine__kpi-number">{{ kpi.value | numberFormat: kpi.decimals }}</strong>
                    <span class="machine__kpi-unit">{{ kpi.unit }}</span>
                  } @else {
                    <strong class="machine__kpi-number">{{ kpi.textKey | translate }}</strong>
                  }
                } @else {
                  <ng-container
                    [ngTemplateOutlet]="signalStateText"
                    [ngTemplateOutletContext]="{ $implicit: kpi }"></ng-container>
                }
              </span>
            </li>
          }
        </ul>
      } @else {
        <div class="notify notify__info machine__notice">
          <i class="agres-info" aria-hidden="true"></i>
          <p>{{ 'monitoring.tabs.machine.engine-not-detected-description' | translate }}</p>
        </div>
      }

      @if (isEngineDetected || machineTab.faults.length > 0) {
        <section class="machine__accordion" #faultsSection>
          <h3 class="machine__accordion-heading">
            <button
              type="button"
              class="machine__accordion-toggle"
              [attr.aria-expanded]="faultsOpen"
              [attr.aria-controls]="idPrefix + '-faults'"
              (click)="toggleFaults()">
              <span class="machine__accordion-title">{{ 'monitoring.tabs.machine.faults.title' | translate }}</span>
              @if (machineTab.activeDtcCount > 0) {
                <span class="machine__chip machine__chip--danger">
                  <i class="agres-alert-triangle" aria-hidden="true"></i>
                  {{
                    pluralKey('monitoring.tabs.machine.faults.active-count', machineTab.activeDtcCount)
                      | translate: { count: machineTab.activeDtcCount }
                  }}
                </span>
              }
              <span class="machine__accordion-summary">
                @for (lamp of machineTab.litLamps; track lamp.labelKey) {
                  <span>{{ lamp.shortLabelKey | translate }}: {{ lamp.stateKey | translate }}</span>
                }
                @if (machineTab.faults.length === 0) {
                  <span>{{ 'monitoring.tabs.machine.faults.none' | translate }}</span>
                }
                @if (machineTab.healthyEcus.length > 0) {
                  <span>{{
                    pluralKey('monitoring.tabs.machine.faults.healthy-ecus', machineTab.healthyEcus.length)
                      | translate: { count: machineTab.healthyEcus.length }
                  }}</span>
                }
              </span>
              <ng-container
                [ngTemplateOutlet]="accordionAction"
                [ngTemplateOutletContext]="{ $implicit: faultsOpen }"></ng-container>
            </button>
          </h3>
          @if (faultsOpen) {
            <div class="machine__accordion-panel" [id]="idPrefix + '-faults'">
              @if (machineTab.faults.length === 0) {
                <p class="machine__empty">{{ 'monitoring.tabs.machine.faults.none' | translate }}</p>
              } @else {
                <div class="machine__fault-grid">
                  @for (fault of machineTab.faultCards; track fault.sourceAddress) {
                    <article class="machine__card machine__fault">
                      <div class="machine__fault-header">
                        <h4 class="machine__card-title">
                          {{ faultTitleKey(fault) | translate: { address: fault.sourceAddress } }}
                        </h4>
                        @if (fault.status === faultStatus.Active) {
                          <span class="machine__chip machine__chip--danger">{{
                            pluralKey('monitoring.tabs.machine.faults.active-count', fault.dtcCount)
                              | translate: { count: fault.dtcCount }
                          }}</span>
                        } @else {
                          <span class="machine__chip machine__chip--muted">{{ fault.statusKey | translate }}</span>
                        }
                      </div>
                      <ul
                        class="machine__lamps"
                        [attr.aria-label]="'monitoring.tabs.machine.faults.lamps-label' | translate">
                        @for (lamp of fault.lamps; track lamp.labelKey) {
                          <li
                            class="machine__chip machine__chip--{{ lampChipModifier(lamp) }}"
                            [attr.title]="lamp.labelKey | translate">
                            @if (lamp.isLit) {
                              <i class="agres-alert-triangle" aria-hidden="true"></i>
                            }
                            {{ lamp.shortLabelKey | translate }}: {{ lamp.stateKey | translate }}
                          </li>
                        }
                      </ul>
                      @if (fault.dtcs.length > 0) {
                        <ol class="machine__dtcs">
                          @for (dtc of fault.dtcs; track $index) {
                            <li class="machine__dtc">
                              <span class="machine__dtc-title">
                                @if (dtc.signalLabelKey) {
                                  {{ dtc.signalLabelKey | translate }}
                                } @else if (dtc.spn !== null) {
                                  {{ 'monitoring.tabs.machine.faults.label-spn' | translate }} {{ dtc.spn }}
                                } @else {
                                  {{ 'monitoring.tabs.machine.faults.unidentified-spn' | translate }}
                                }
                              </span>
                              <span>{{ dtc.fmiKey | translate }}</span>
                              <span class="machine__dtc-meta">
                                @if (dtc.spn !== null) {
                                  {{ 'monitoring.tabs.machine.faults.label-spn' | translate }} {{ dtc.spn }} ·
                                }
                                {{ 'monitoring.tabs.machine.faults.label-fmi' | translate }} {{ dtc.fmi }}
                                @if (dtc.occurrenceCount !== null) {
                                  ·
                                  {{
                                    pluralKey('monitoring.tabs.machine.faults.occurrences', dtc.occurrenceCount)
                                      | translate: { count: dtc.occurrenceCount }
                                  }}
                                }
                              </span>
                            </li>
                          }
                        </ol>
                      }
                      @if (fault.hiddenDtcCount > 0) {
                        <p class="machine__note">
                          {{
                            'monitoring.tabs.machine.faults.hidden-dtcs' | translate: { count: fault.hiddenDtcCount }
                          }}
                        </p>
                      }
                    </article>
                  }
                  @if (machineTab.healthyEcus.length > 0) {
                    <article class="machine__card machine__healthy">
                      <h4 class="machine__card-title machine__card-title--ok">
                        <i class="agres-check" aria-hidden="true"></i>
                        {{ 'monitoring.tabs.machine.faults.status.no-active-faults' | translate }}
                      </h4>
                      <p class="machine__ecu-list">
                        @for (fault of machineTab.healthyEcus; track fault.sourceAddress) {
                          <span>{{ faultTitleKey(fault) | translate: { address: fault.sourceAddress } }}</span>
                        }
                      </p>
                    </article>
                  }
                </div>
              }
            </div>
          }
        </section>
      }

      @if (isEngineDetected) {
        <section class="machine__accordion">
          <h3 class="machine__accordion-heading">
            <button
              type="button"
              class="machine__accordion-toggle"
              [attr.aria-expanded]="signalsOpen"
              [attr.aria-controls]="idPrefix + '-signals'"
              (click)="toggleSignals()">
              <span class="machine__accordion-title">{{ 'monitoring.tabs.machine.signals-title' | translate }}</span>
              <span class="machine__accordion-summary">
                <span>{{
                  'monitoring.tabs.machine.signals-summary'
                    | translate: { total: machineTab.signalCount, unread: machineTab.unreadSignalCount }
                }}</span>
              </span>
              <ng-container
                [ngTemplateOutlet]="accordionAction"
                [ngTemplateOutletContext]="{ $implicit: signalsOpen }"></ng-container>
            </button>
          </h3>
          @if (signalsOpen) {
            <div class="machine__accordion-panel" [id]="idPrefix + '-signals'">
              <div class="machine__groups">
                @for (column of machineTab.signalColumns; track $index) {
                  <div class="machine__group-column">
                    @for (group of column; track group.id) {
                      <div class="machine__card machine__group">
                        <h4 class="machine__group-title">{{ group.titleKey | translate }}</h4>
                        <dl class="machine__signals">
                          @for (signal of group.signals; track signal.spn) {
                            <div class="machine__signal">
                              <dt>{{ signal.shortLabelKey | translate }}</dt>
                              <dd>
                                @if (signal.state === signalState.Valid) {
                                  <strong>
                                    @if (signal.value !== null) {
                                      {{ signal.value | numberFormat: signal.decimals }} {{ signal.unit }}
                                    } @else {
                                      {{ signal.textKey | translate }}
                                    }
                                  </strong>
                                } @else {
                                  <ng-container
                                    [ngTemplateOutlet]="signalStateText"
                                    [ngTemplateOutletContext]="{ $implicit: signal }"></ng-container>
                                }
                              </dd>
                            </div>
                          }
                        </dl>
                      </div>
                    }
                  </div>
                }
              </div>
            </div>
          }
        </section>
      }
    }
  </section>
}

<ng-template #signalStateText let-signal>
  <span class="machine__state machine__state--{{ signal.state }}">
    @if (signal.state === signalState.Stale) {
      <i class="agres-clock" aria-hidden="true"></i>
    }
    @if (signal.state === signalState.Error) {
      <i class="agres-octagon" aria-hidden="true"></i>
    }
    {{ signal.textKey | translate }}
  </span>
</ng-template>

<ng-template #accordionAction let-open>
  <span class="machine__accordion-action">
    @if (open) {
      {{ 'monitoring.tabs.machine.collapse' | translate }}
      <i class="agres-arrow-up" aria-hidden="true"></i>
    } @else {
      {{ 'monitoring.tabs.machine.expand' | translate }}
      <i class="agres-arrow-down" aria-hidden="true"></i>
    }
  </span>
</ng-template>
